/**
 * Response of GET /v1/reference/calendar — the reference Worker
 * (cf-workers/reference/src/calendar.ts). A slow-moving US-equity trading calendar: the dates the
 * exchange is FULLY closed plus the half-days it closes early, published by the exchange and served
 * from the same tradedata origin the realtime session guard reads.
 *
 * Full closures cover the announcements no rule can derive — presidential funerals, weather
 * closures — which is exactly the half `usEquityCalendar`'s arithmetic can never produce. Early
 * closes carry a per-date close minute so an ad-hoc half-day (a same-day weather-driven early
 * close) is honoured, not just the three recurring rules.
 */
export interface MarketEarlyClose {
  /** ET date (YYYY-MM-DD). */
  date: string;
  /** Close time as minutes since ET midnight, e.g. 780 = 13:00. */
  closeMin: number;
  /** "HH:MM" ET convenience derived from closeMin, e.g. "13:00". */
  closeTime: string;
}

export interface MarketCalendarResponse {
  /** ET dates (YYYY-MM-DD), ascending, that the US equity market is/was fully closed. */
  closed: string[];
  /** Half-days — still trading days, but the session ends early. Ascending by date. */
  earlyCloses: MarketEarlyClose[];
  /** Regular session close as minutes since ET midnight (e.g. 960 = 16:00), or null. */
  regularCloseMin: number | null;
  /** "HH:MM" ET convenience for regularCloseMin, or null. */
  regularCloseTime: string | null;
  counts: { closed: number; earlyCloses: number };
  /** False until the upstream vendor calendar has loaded on the origin. */
  loaded: boolean;
  /** Provenance — currently the string `'insiderfinance'`. */
  source: string;
}

const MINUTES_IN_DAY = 24 * 60;

const isCloseMinute = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0 && value < MINUTES_IN_DAY;

const isEarlyClose = (value: unknown): value is MarketEarlyClose => {
  if (!value || typeof value !== 'object') return false;
  const early = value as Partial<MarketEarlyClose>;
  return (
    typeof early.date === 'string' &&
    isCloseMinute(early.closeMin) &&
    typeof early.closeTime === 'string'
  );
};

/**
 * Structural guard for the reference-calendar payload. Deliberately strict on the two fields the
 * session model installs — `closed` and `earlyCloses` — and lenient on the derived conveniences, so
 * a partial or evolving origin can never install a malformed date or minute. A payload that fails
 * this check is dropped and the computed rules stay in place.
 */
export const isMarketCalendarResponse = (value: unknown): value is MarketCalendarResponse => {
  if (!value || typeof value !== 'object') return false;
  const response = value as Partial<MarketCalendarResponse>;
  return (
    Array.isArray(response.closed) &&
    response.closed.every((date) => typeof date === 'string') &&
    Array.isArray(response.earlyCloses) &&
    response.earlyCloses.every(isEarlyClose) &&
    (response.regularCloseMin === null || isCloseMinute(response.regularCloseMin)) &&
    typeof response.loaded === 'boolean'
  );
};
